import {createRoot} from 'react-dom/client';
import {createContext, useContext} from 'react';
import * as WeirWood from '@baidu/weirwood-sdk';
import {setTracker} from '@baidu/legion-eop-track';
import App from '@/modules/App';
import {initBceStorage} from '@/api/ThirdParty/ocpc';
import '@/styles';
import {WeirwoodErrorBoundary} from '@/components/ErrorBoundary/ErrorBoundary';
import {getH5TicketUrl, isLoadByIframe, isMobileUserAgent, isSandbox} from '@/utils/util';
import {PC_TICKET_CREATE} from '@/dicts/qualify';
import client from '@/api/client';
import {requiredAuthUrl} from '@/links/auth';
import {HOST_CONFIG} from '@/modules/Qualify/common/constant';
import {isVirtualUser} from '@/utils/vcp';

const FrameworkContext = createContext<any>(null!);

async function initFrameworkByDefault(locale: string) {
    // 不需要鉴权的页面不请求constantV3
    const constants = requiredAuthUrl(location) && !isVirtualUser()
        ? await client.constantV3(locale) : Promise.resolve({
            domains: {
                console: isSandbox() ? HOST_CONFIG.sandbox : HOST_CONFIG.online,
            },
        });
    return {
        flags: {
            is_online: !!(/bce.baidu/.exec(location?.hostname)),
        },
        constants: {
            ...constants,
            ticketUrl: /bce.baidu/.exec(location?.hostname)
                ? (isMobileUserAgent() ? getH5TicketUrl() : `https://ticket.bce.baidu.com${PC_TICKET_CREATE}`)
                : (isMobileUserAgent() ? getH5TicketUrl() : `https://ticket.bcetest.baidu.com${PC_TICKET_CREATE}`),
        },
    };
}

async function initFramework() {
    let constants = {};
    const locale = window?.framework?.i18n?.getCurrentLanguage?.() || 'zh-cn';
    // 隐藏fe-framework的header
    if (['/qualify/h5/'].includes(location?.pathname)
    || window.navigator.userAgent?.toLowerCase()?.includes('bai du yun')
    || isLoadByIframe()) {
        constants = await initFrameworkByDefault(locale);
        const frameworkHead = document.getElementById('header');
        frameworkHead && frameworkHead.setAttribute('style', 'display: none');
        const frameworkMain = document.getElementById('main');
        frameworkMain && frameworkMain.setAttribute('style', 'top: 0;');
    }
    else {
        try {
            constants = await window?.framework?.boot();
            if (isMobileUserAgent() && (constants as any).constants) {
                (constants as any).constants.ticketUrl = getH5TicketUrl();
            }
            else {
                (constants as any).constants.ticketUrl += PC_TICKET_CREATE;
            }
        } catch (e) {
            constants = await initFrameworkByDefault(locale);
        }
    }
    return {
        ...constants,
        locale,
    };
}

function initFEMonitor() {
    try {
        const Monitor = window.Monitor;
        Monitor && Monitor.default?.init();
    } catch (e) {
        console.error(e);
    }
}

function initWeirWood() {
    // console-qualify-h5项目
    const options = {
        common: {
            buildid: '209',
            token: '9cf86b9c9d8a4ff68c3b36cb22e86fa1',
            ignoreUrls: [
                // 本地和沙盒开发和报错不发送
                'qasandbox.bcetest.baidu.com',
                'localhost',
                '127.0.0.1',
            ],
        },
        error: {
            collectWindowErrors: true,
            collectUnhandledRejections: true,
        },
        perf: {
            // 性能数据PV日志会比较大，可以输入 sampleRate 进行采样(值为0~1之间)，控制在 50 W左右
            sampleRate: 1,
            spa: true,
            history: true,
        },
    };
    try {
        WeirWood.init(options);
    } catch (e) {
        console.error(e);
    }
}

export function useFramework() {
    return useContext<any>(FrameworkContext);
}


export async function main() {
    // 接入前端监控平台
    initWeirWood();
    const framework: any = await initFramework();
    // 接入百度统计
    setTracker('1d218938b987e1cd56daa5e45102d928');
    // 接入数仓统计
    initFEMonitor();
    // OCPC统计
    initBceStorage(framework?.flags?.is_online || location.host.includes('bce.baidu'));
    window.framework = framework;
    const parent = document.getElementById('main') || document.body;
    const root = createRoot(parent);
    root.render(
        <WeirwoodErrorBoundary>
            <FrameworkContext.Provider value={framework}>
                <App />
            </FrameworkContext.Provider>
        </WeirwoodErrorBoundary>
    );
}
